import {
  breadcrumbsIntegration,
  browserApiErrorsIntegration,
  captureConsoleIntegration,
  captureException,
  contextLinesIntegration,
  debugIntegration,
  dedupeIntegration,
  extraErrorDataIntegration,
  functionToStringIntegration,
  globalHandlersIntegration,
  httpClientIntegration,
  httpContextIntegration,
  inboundFiltersIntegration,
  init,
  linkedErrorsIntegration,
  moduleMetadataIntegration,
  replayIntegration,
  reportingObserverIntegration,
  sessionTimingIntegration,
  browserTracingIntegration as vueBrowserTracingIntegration,
  withScope
} from "@sentry/vue";
import defu from "defu";
import { defineNuxtPlugin, useAppConfig, useRuntimeConfig } from "nuxt/app";
const integrationMap = {
  Breadcrumbs: breadcrumbsIntegration,
  BrowserTracing: vueBrowserTracingIntegration,
  CaptureConsole: captureConsoleIntegration,
  ContextLines: contextLinesIntegration,
  Debug: debugIntegration,
  Dedupe: dedupeIntegration,
  ExtraErrorData: extraErrorDataIntegration,
  FunctionToString: functionToStringIntegration,
  GlobalHandlers: globalHandlersIntegration,
  HttpClient: httpClientIntegration,
  HttpContext: httpContextIntegration,
  InboundFilters: inboundFiltersIntegration,
  LinkedErrors: linkedErrorsIntegration,
  ModuleMetadata: moduleMetadataIntegration,
  Replay: replayIntegration,
  ReportingObserver: reportingObserverIntegration,
  SessionTiming: sessionTimingIntegration,
  TryCatch: browserApiErrorsIntegration
};
const defaultIntegrations = [
  "Breadcrumbs",
  "BrowserTracing",
  "Debug",
  "FunctionToString",
  "GlobalHandlers",
  "HttpContext",
  "InboundFilters",
  "LinkedErrors",
  "TryCatch"
];
export default defineNuxtPlugin({
  enforce: "pre",
  setup(nuxt) {
    const runtimeSentryConfig = useRuntimeConfig().public.sentry;
    const appSentryConfig = useAppConfig().sentry;
    const enabled = runtimeSentryConfig?.enabled ?? !process.dev;
    const dsn = runtimeSentryConfig?.dsn;
    if (!dsn) {
      console.warn("Sentry DSN is not provided, Sentry will not be initialized.");
      return;
    }
    const runtimeSdkConfig = runtimeSentryConfig?.clientSdk;
    const runtimeIntegrations = runtimeSentryConfig?.clientIntegrations;
    const appSdkConfig = appSentryConfig?.clientSdk;
    const appIntegrations = appSentryConfig?.clientIntegrations;
    const appConfig = typeof appSdkConfig === "function" ? appSdkConfig(nuxt.vueApp.$nuxt) : appSdkConfig;
    const integrationConfig = defu(runtimeIntegrations, appIntegrations);
    const config = defu(runtimeSdkConfig, appConfig, {
      tracesSampleRate: 1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
      environment: process.dev ? "development" : "production",
      enabled
    });
    const integrations = config.integrations ?? buildIntegrations(integrationConfig, nuxt);
    init({
      app: nuxt.vueApp,
      dsn,
      ...config,
      integrations
    });
    nuxt.vueApp.config.errorHandler = (err, context) => {
      withScope((scope) => {
        scope.setExtra("context", context);
        captureException(err);
      });
    };
    nuxt.hook("app:error", (err) => {
      captureException(err);
    });
  }
});
function buildIntegrations(integrationConfig, nuxtApp) {
  const integrations = [];
  for (const [name, integration] of Object.entries(integrationMap)) {
    const options = integrationConfig[name];
    if (options === false)
      continue;
    if (options == null && !defaultIntegrations.includes(name))
      continue;
    const integrationOptions = options === true ? void 0 : options;
    if (name === "BrowserTracing") {
      const browserOptions = integrationOptions;
      integrations.push(
        vueBrowserTracingIntegration({
          ...browserOptions,
          router: nuxtApp.vueApp.$nuxt.$router
        })
      );
      continue;
    }
    integrations.push(integration(options === true ? void 0 : options));
  }
  return integrations;
}
